import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { append, insertItem, patch, updateItem } from '@ngxs/store/operators';
import { tap } from 'rxjs/operators';
import { AdminCompetenceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { ICompetenceGroupModel } from '../model/competence-group.model';
import { CompetenceGroupApiService } from '../services/competence-group-api.service';
import {
  AddCompetenceGroup,
  DeleteCompetenceGroup,
  GetCompetenceGroupById,
  LoadAllCompetenceGroups,
  LoadCompetenceGroups,
  RestoreCompetenceGroup,
  UpdateCompetenceGroup,
  ClearCurrentCompetenceGroup,
} from './competence-group.actions';

export interface CompetenceGroupStateModel {
  loaded: boolean;
  competenceGroups: ICompetenceGroupModel[] | undefined;
  allCompetenceGroups: IdName[];
  includeDeletedCompetenceGroups: boolean;
  competenceGroupTotalPages: number;
  currentCompetenceGroup: ICompetenceGroupModel | undefined;
}

const COMPETENCE_GROUP_STATE_TOKEN = new StateToken<CompetenceGroupStateModel>(
  'competenceGroupState'
);

@State<CompetenceGroupStateModel>({
  name: COMPETENCE_GROUP_STATE_TOKEN,
  defaults: {
    loaded: false,
    competenceGroups: undefined,
    allCompetenceGroups: [],
    includeDeletedCompetenceGroups: false,
    competenceGroupTotalPages: 0,
    currentCompetenceGroup: undefined,
  },
})
@Injectable()
export class CompetenceGroupState {
  constructor(private service: CompetenceGroupApiService) {}

  @Action(LoadCompetenceGroups, { cancelUncompleted: true })
  loadCompetenceGroups(
    { getState, setState }: StateContext<CompetenceGroupStateModel>,
    { includeDeleted, filter }: LoadCompetenceGroups
  ) {
    const includeDeletedChecker = includeDeleted || getState().includeDeletedCompetenceGroups;
    return this.service.getCompetenceGroups(includeDeletedChecker, filter).pipe(
      tap((response: ApiResponse<ICompetenceGroupModel[]>) => {
        const state = getState();
        setState({
          ...state,
          loaded: true,
          competenceGroups: response.data,
          competenceGroupTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllCompetenceGroups, { cancelUncompleted: true })
  loadAllCompetenceGroups(ctx: StateContext<CompetenceGroupStateModel>) {
    return this.service.getAllCompetenceGroups().pipe(
      tap((allCompetenceGroups: IdName[]) => {
        ctx.setState(patch({ allCompetenceGroups }));
      })
    );
  }

  @Action(AddCompetenceGroup, { cancelUncompleted: true })
  @OnSuccess({ url: AdminCompetenceRoutes.Groups, message: 'competenceGroupCreated' })
  addCompetenceGroup(
    ctx: StateContext<CompetenceGroupStateModel>,
    { competenceGroup }: AddCompetenceGroup
  ) {
    return this.service.addCompetenceGroup(competenceGroup).pipe(
      tap((response: ICompetenceGroupModel) => {
        ctx.setState(
          patch({
            competenceGroups: append([response]),
          })
        );
      })
    );
  }

  @Action(GetCompetenceGroupById, { cancelUncompleted: true })
  getCompetenceGroupById(
    ctx: StateContext<CompetenceGroupStateModel>,
    { id }: GetCompetenceGroupById
  ) {
    // getting every existing competence group from state
    const competenceGroupsFromState = ctx.getState().competenceGroups;
    // if a competence group exist then just get updated (latest) from api
    if (competenceGroupsFromState?.find((cg: ICompetenceGroupModel) => cg.id === id)) {
      return this.service.getCompetenceGroupById(id).pipe(
        tap((response: ICompetenceGroupModel) => {
          ctx.setState(
            patch({
              competenceGroups: updateItem<ICompetenceGroupModel>(
                (toGet: ICompetenceGroupModel | undefined) => toGet?.id === response.id,
                response
              ),
              currentCompetenceGroup: response,
            })
          );
        })
      );
    }
    // if it does not exist then fetch it from the api
    return this.service.getCompetenceGroupById(id).pipe(
      tap((response: ICompetenceGroupModel) => {
        ctx.setState(
          patch({
            competenceGroups: insertItem<ICompetenceGroupModel>(response),
            currentCompetenceGroup: response,
          })
        );
      })
    );
  }

  @Action(UpdateCompetenceGroup, { cancelUncompleted: true })
  @OnSuccess({ url: AdminCompetenceRoutes.Groups, message: 'competenceGroupUpdated' })
  updateCompetenceGroup(
    ctx: StateContext<CompetenceGroupStateModel>,
    { competenceGroup }: UpdateCompetenceGroup
  ) {
    return this.service.updateCompetenceGroup(competenceGroup).pipe(
      tap((response: ICompetenceGroupModel) => {
        ctx.setState(
          patch({
            competenceGroups: updateItem<ICompetenceGroupModel>(
              (toUpdate: ICompetenceGroupModel | undefined) => toUpdate?.id === response.id,
              response
            ),
          })
        );
      })
    );
  }

  @Action(DeleteCompetenceGroup, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminCompetenceRoutes.Groups,
    message: 'competenceGroupDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteCompetenceGroup(
    ctx: StateContext<CompetenceGroupStateModel>,
    { id }: DeleteCompetenceGroup
  ) {
    return this.service.deleteCompetenceGroup(id);
  }

  @Action(RestoreCompetenceGroup, { cancelUncompleted: true })
  @OnSuccess({ url: AdminCompetenceRoutes.Groups, message: 'competenceGroupRestored' })
  restoreCompetenceGroup(
    ctx: StateContext<CompetenceGroupStateModel>,
    { id }: RestoreCompetenceGroup
  ) {
    return this.service.restoreCompetenceGroup(id);
  }

  @Action(ClearCurrentCompetenceGroup)
  clearCurrentCompetenceGroup(ctx: StateContext<CompetenceGroupStateModel>) {
    ctx.setState(patch({ currentCompetenceGroup: undefined }));
  }

  @Selector()
  static competenceGroupsLoaded(state: CompetenceGroupStateModel) {
    return state.loaded;
  }

  @Selector()
  static getCompetenceGroupTotalPages(state: CompetenceGroupStateModel) {
    return state.competenceGroupTotalPages;
  }

  @Selector()
  static getCompetenceGroups(state: CompetenceGroupStateModel) {
    return state.competenceGroups;
  }

  @Selector()
  static getAllCompetenceGroups(state: CompetenceGroupStateModel): IdName[] {
    return state.allCompetenceGroups;
  }

  @Selector()
  static getCompetenceGroupById(state: CompetenceGroupStateModel) {
    return (id: string) => {
      return state.competenceGroups?.find(
        (competenceGroups: ICompetenceGroupModel) => competenceGroups.id === id
      );
    };
  }

  @Selector()
  static getCurrentCompetenceGroup(state: CompetenceGroupStateModel) {
    return state.currentCompetenceGroup;
  }
}
